import React from 'react';
import { Competence, Service } from '../../../../../../shared/types/config';

interface CompetencesTabProps {
  competences: Competence[];
  searchCompetence: string;
  onSearchChange: (value: string) => void;
  filterType: 'all' | 'normal' | 'specialite';
  onFilterTypeChange: (value: 'all' | 'normal' | 'specialite') => void;
  filterService: number | 'all';
  onFilterServiceChange: (value: number | 'all') => void;
  onAdd: () => void;
  onEdit: (competence: Competence) => void;
  onDelete: (id: number) => void;
  services: Service[];
}

const CompetencesTab: React.FC<CompetencesTabProps> = ({
  competences,
  searchCompetence,
  onSearchChange,
  filterType,
  onFilterTypeChange,
  filterService,
  onFilterServiceChange,
  onAdd,
  onEdit,
  onDelete,
  services,
}) => {
  const filteredCompetences = competences.filter(c => {
    const matchesSearch = c.nom.toLowerCase().includes(searchCompetence.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(searchCompetence.toLowerCase());
    const matchesType = filterType === 'all' || 
      (filterType === 'normal' && !c.is_specialite) ||
      (filterType === 'specialite' && c.is_specialite);
    const matchesService = filterService === 'all' || c.id_service === filterService;
    return matchesSearch && matchesType && matchesService;
  });

  return (
    <div className="space-y-6">
      {/* Barre de recherche, filtres et bouton ajouter */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Rechercher une compétence..."
              value={searchCompetence}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={onAdd}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Ajouter
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-300">Type:</label>
            <select
              value={filterType}
              onChange={(e) => onFilterTypeChange(e.target.value as 'all' | 'normal' | 'specialite')}
              className="px-3 py-1 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous</option>
              <option value="normal">Compétences normales</option>
              <option value="specialite">Spécialités</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-300">Service:</label>
            <select
              value={filterService}
              onChange={(e) => onFilterServiceChange(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="px-3 py-1 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>{service.nom}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Liste des compétences */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-gray-800 border-b border-gray-700">
            <tr>
              <th className="px-4 py-3 text-gray-300 font-semibold">ID</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Ordre</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Nom</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Type</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Service</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Description</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompetences.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  Aucune compétence trouvée
                </td>
              </tr>
            ) : (
              filteredCompetences.map((competence) => {
                const service = services.find(s => s.id === competence.id_service);
                return (
                  <tr key={competence.id} className="border-b border-gray-700 hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-gray-300">{competence.id}</td>
                    <td className="px-4 py-3 text-gray-300">
                      {competence.ordre !== undefined && competence.ordre !== null ? (
                        <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs border border-green-500/50 font-mono">
                          {competence.ordre}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white font-medium">{competence.nom}</td>
                    <td className="px-4 py-3 text-gray-300">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        competence.is_specialite 
                          ? 'bg-purple-500/20 text-purple-300' 
                          : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {competence.is_specialite ? 'Spécialité' : 'Compétence'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {service ? service.nom : competence.id_service ? `Service #${competence.id_service}` : 'Générale'}
                    </td>
                    <td className="px-4 py-3 text-gray-300 max-w-xs truncate">
                      {competence.description || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onEdit(competence)}
                          className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => competence.id && onDelete(competence.id)}
                          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompetencesTab;

